import ApiController from '@/services/ApiController'
import { useCustomerStore } from '@/stores/customer'
import { PaymentMethodData } from '@/types/payment'
import { defineStore, storeToRefs } from 'pinia'
import { toRaw } from 'vue'
import { useAddressesStore } from './addresses'

interface PaymentStoreState {
  paymentMethods: any[] | null
  availablePaymentMethods?: any[] | null
}

interface ThreeDSecureCheckData {
  shippingAddressId?: number | null
  shippingAddressData?: object | null
  billingAddressId?: number | null
  billingAddressData?: object | null
  paymentMethodId?: number | null
  paymentMethodData?: PaymentMethodData | null
  email: string | null
  amount: number
  isGuestCheckout?: boolean
}

export const usePaymentStore = defineStore('payment', {
  state: (): PaymentStoreState => ({
    paymentMethods: null,
    availablePaymentMethods: null
  }),
  getters: {
    hasPaymentMethods: ({ paymentMethods }): boolean =>
      !!paymentMethods && paymentMethods.length > 0,
    getPaymentMethodById:
      ({ paymentMethods }) =>
      (id: number): PaymentMethodData | null => {
        return paymentMethods
          ? (paymentMethods.find(
              (method) => method.customer_payment_method_id === id
            ) ?? null)
          : null
      },
    getDefaultPaymentMethod: ({ paymentMethods }) => {
      if (!paymentMethods || paymentMethods.length === 0) return null
      const defaultPaymentMethod = paymentMethods.find(
        (a) => a.is_default_payment_method
      )
      return defaultPaymentMethod ?? paymentMethods[0]
    },
    isPaymentMethodAvailable: ({ availablePaymentMethods }) => {
      return (methodCode: string): boolean => {
        if (!availablePaymentMethods) return false
        return availablePaymentMethods.includes(methodCode)
      }
    }
  },
  actions: {
    async fetch() {
      const customerStore = useCustomerStore()
      const { customerActive } = storeToRefs(customerStore)
      await customerStore.waitUntilPending()

      try {
        let paymentMethodsPromise = Promise.resolve([])
        if (customerActive.value) {
          paymentMethodsPromise = ApiController.fetchCustomerPaymentMethods()
        }
        const [available, paymentMethods] = await Promise.all([
          ApiController.fetchAvailablePaymentMethods(),
          paymentMethodsPromise
        ])

        this.availablePaymentMethods = _sortPaymentMethods(available)
        // For guest users, set to empty array instead of null so loading state clears
        this.paymentMethods = paymentMethods || []
      } catch (e) {
        console.error('Error fetching payment methods:', e)
        // On error, set to empty array for guest users
        if (!customerActive.value) {
          this.paymentMethods = []
        }
      }
    },
    async savePaymentMethod(data: PaymentMethodData): Promise<number | null> {
      // Prepayment doesn't need to be saved
      if (data.method_code === 'prepayment') {
        return null
      }

      try {
        delete data.customer_payment_method_id
        const response = await ApiController.savePaymentMethod(data)
        await this.fetch()

        if (!response?.data?.id) {
          return null
        }
        return response?.data?.id
      } catch (e) {
        await ApiController.logPaymentError(data.method_code || 'unknown', e)
        throw e
      }
    },
    async updatePaymentMethod(
      id: number,
      data: PaymentMethodData
    ): Promise<void> {
      try {
        await ApiController.updateCustomerPaymentMethod(id, data)
        await this.fetch()
      } catch (e) {
        await ApiController.logPaymentError(data.method_code || 'unknown', e)
        throw e
      }
    },
    async performThreeDSecureCheck(
      data: ThreeDSecureCheckData
    ): Promise<string | null> {
      const addressesStore = useAddressesStore()
      const paymentStore = usePaymentStore()

      let {
        shippingAddressId,
        shippingAddressData,
        billingAddressId,
        billingAddressData,
        paymentMethodId,
        paymentMethodData,
        email,
        amount
      } = data

      paymentMethodData = _resolvePaymentMethodData(
        paymentMethodData,
        paymentMethodId,
        paymentStore
      )

      if (paymentMethodData?.method_code !== 'credit_card') {
        return null
      }

      shippingAddressData = _resolveAddressData(
        shippingAddressData,
        shippingAddressId,
        addressesStore
      )

      billingAddressData = _resolveAddressData(
        billingAddressData,
        billingAddressId,
        addressesStore
      )

      if (!shippingAddressData || !billingAddressData || !paymentMethodData) {
        throw new Error('Insufficient data for 3D Secure check')
      }

      const { nonce, bin, ipAddress } = data.isGuestCheckout
        ? await _getGuestCheckout3DSData(paymentMethodData)
        : await _getAuthenticated3DSData(paymentMethodData)

      const { threeDSecureCheck } = await import('@/services/Braintree')

      try {
        const { nonce: tokenizedNonce } = await threeDSecureCheck({
          shippingAddress: shippingAddressData,
          billingAddress: billingAddressData,
          nonce,
          email,
          amount,
          bin,
          ipAddress
        })
        return tokenizedNonce
      } catch (e) {
        await ApiController.logPaymentError('credit_card', e)
        throw e
      }
    },
    cleanPaymentMethodForApi(
      paymentMethod: Record<string, any> | undefined
    ): Record<string, any> | undefined {
      if (!paymentMethod) {
        return undefined
      }

      // Remove sensitive/internal fields that shouldn't be sent to API
      const {
        bin,
        card_bin,
        tokenized_nonce,
        nonce,
        customer_payment_method_id,
        ...cleanedPaymentMethod
      } = paymentMethod

      return cleanedPaymentMethod
    }
  }
})

const _resolvePaymentMethodData = (
  paymentMethodData: any,
  paymentMethodId: number | null | undefined,
  paymentStore: any
) => {
  if (!paymentMethodData && paymentMethodId) {
    return toRaw(paymentStore.getPaymentMethodById(paymentMethodId))
  }
  return paymentMethodData
}

const _resolveAddressData = (
  addressData: any,
  addressId: number | null | undefined,
  addressesStore: any
) => {
  if (!addressData && addressId) {
    return toRaw(addressesStore.getAddressById(addressId))
  }
  return addressData
}

const _getGuestCheckout3DSData = async (paymentMethodData: any) => {
  const guestData = paymentMethodData as Record<string, any>
  const nonce = guestData.tokenized_nonce || guestData.nonce
  const bin = guestData.bin || guestData.card_bin
  const ipAddress = guestData.ip_address

  if (!nonce) throw new Error('No nonce available for 3D Secure check')
  if (!bin) throw new Error('BIN (Bank Identification Number) is required')

  return {
    nonce,
    bin,
    ipAddress: ipAddress || undefined
  }
}

const _getAuthenticated3DSData = async (paymentMethodData: any) => {
  const authData = paymentMethodData as Record<string, any>

  if (authData.token) {
    const {
      data: { braintree_method_nonce, ip_address, bin: apiBin }
    } = await ApiController.fetchBraintreeMethodNonce({
      token: authData.token
    })

    return {
      nonce: braintree_method_nonce,
      bin: apiBin,
      ipAddress: ip_address
    }
  }

  const nonce = authData.tokenized_nonce || authData.nonce
  const bin = authData.bin || authData.card_bin
  const ipAddress = authData.ip_address

  if (!nonce) throw new Error('No nonce available for 3D Secure check')
  if (!bin) throw new Error('BIN (Bank Identification Number) is required')

  return {
    nonce,
    bin,
    ipAddress: ipAddress || undefined
  }
}

const _sortPaymentMethods = (methods: string[]) => {
  const priorityMethodsOrder = ['purchase_order', 'prepayment']
  const filtered = methods.filter(
    (item: string) => !priorityMethodsOrder.includes(item)
  )

  const foundPriorityMethods = priorityMethodsOrder.filter((method: string) =>
    methods.includes(method)
  )
  if (foundPriorityMethods.length === 0) return methods

  // Return priority methods first (in order: purchase_order, then prepayment), then rest
  return [...foundPriorityMethods, ...filtered]
}
